<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { collection, onSnapshot, query, orderBy, type Unsubscribe } from 'firebase/firestore'
import { db } from '@/firebase'
import { useOpsStore } from '@/stores/ops'
import { useCommsStore } from '@/stores/comms'
import { useIAPStore } from '@/stores/iap'
import { useAuthStore } from '@/stores/auth'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import type { AgencyChannelPreset, CommsChannel } from '@/types'

const props = defineProps<{ incidentId: string }>()
defineEmits<{ prev: []; next: [] }>()

const opsStore = useOpsStore()
const commsStore = useCommsStore()
const iapStore = useIAPStore()
const authStore = useAuthStore()

const opId = computed(() => opsStore.selectedOpId ?? '')
const op = computed(() => opsStore.selectedOp)

// Channel presets from agency
const presets = ref<AgencyChannelPreset[]>([])
const showLibraryDialog = ref(false)
const agencyId = computed(() => authStore.userProfile?.agency_id ?? '')

let unsubPresets: Unsubscribe | null = null

onMounted(() => {
  if (!agencyId.value) return
  const q = query(
    collection(db, 'agencies', agencyId.value, 'channel_presets'),
    orderBy('order'),
  )
  unsubPresets = onSnapshot(q, (snap) => {
    presets.value = snap.docs.map((d) => ({ preset_id: d.id, ...d.data() }) as AgencyChannelPreset)
  })
})

onUnmounted(() => {
  if (unsubPresets) { unsubPresets(); unsubPresets = null }
})

async function addFromPreset(preset: AgencyChannelPreset) {
  if (!opId.value) return
  await commsStore.addChannel(props.incidentId, opId.value, {
    name: preset.name,
    assignment: '',
    rx_freq: preset.rx_freq,
    rx_tone: preset.rx_tone,
    tx_freq: preset.tx_freq,
    tx_tone: preset.tx_tone,
    mode: preset.mode,
    remarks: preset.remarks,
    zone_group: '',
    channel_number: '',
  } as Omit<CommsChannel, 'channel_id' | 'order' | 'created_at'>)
  showLibraryDialog.value = false
}

async function addBlankRow() {
  if (!opId.value) return
  await commsStore.addChannel(props.incidentId, opId.value, {
    name: '',
    assignment: '',
    zone_group: '',
    channel_number: '',
  })
}

async function updateCell(channelId: string, field: string, value: string) {
  if (!opId.value) return
  await commsStore.updateChannel(props.incidentId, opId.value, channelId, { [field]: value } as Partial<CommsChannel>)
}

async function removeRow(channelId: string) {
  if (!opId.value) return
  await commsStore.removeChannel(props.incidentId, opId.value, channelId)
}

// ICP Phone
async function saveIcpPhone(value: string) {
  if (!opId.value) return
  await opsStore.updateOp(props.incidentId, opId.value, { contact_phone: value })
}

// Prepared by
const preparedBy205 = computed(() => iapStore.iap?.prepared_by_205 ?? '')
async function savePreparedBy(value: string) {
  if (!opId.value) return
  await iapStore.savePreparedBy(props.incidentId, opId.value, '205', value)
}

const COLUMNS = [
  { key: 'zone_group', label: 'Zone/Group', width: '80px' },
  { key: 'channel_number', label: 'Ch #', width: '60px' },
  { key: 'assignment', label: 'Function', width: '100px' },
  { key: 'name', label: 'Channel Name', width: '120px' },
  { key: 'rx_freq', label: 'RX Freq', width: '90px' },
  { key: 'rx_tone', label: 'RX Tone', width: '80px' },
  { key: 'tx_freq', label: 'TX Freq', width: '90px' },
  { key: 'tx_tone', label: 'TX Tone', width: '80px' },
  { key: 'mode', label: 'Mode', width: '60px' },
  { key: 'remarks', label: 'Remarks', width: '120px' },
] as const
</script>

<template>
  <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
    <!-- Section head -->
    <div class="flex items-end gap-3.5 flex-wrap">
      <div class="flex flex-col gap-0.5">
        <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 205 · Required</span>
        <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Radio Communications Plan</h2>
      </div>
      <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Channel assignments, frequencies, and ICP contact for this operational period.</p>
    </div>

    <!-- Channels group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Radio Channels</h3>
        <div class="ml-auto flex gap-1.5">
          <button class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-2.5 py-1.5 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface hover:bg-surface-container-low transition-colors" @click="showLibraryDialog = true">
            <span class="material-symbols-outlined text-[14px]">library_add</span>
            Library
          </button>
          <button class="inline-flex items-center gap-1.5 bg-surface border border-dashed border-border-strong rounded px-2.5 py-1.5 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary hover:border-primary transition-colors" @click="addBlankRow">
            <span class="material-symbols-outlined text-[14px]">add</span>
            Blank Row
          </button>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full border-collapse" style="font-size: 12px;">
          <thead>
            <tr>
              <th v-for="col in COLUMNS" :key="col.key" :style="{ minWidth: col.width }" class="px-2 py-2 text-left font-mono text-[9px] tracking-[0.10em] uppercase text-outline font-medium bg-surface-container-low border-b border-border border-r border-border/40 last:border-r-0 whitespace-nowrap">
                {{ col.label }}
              </th>
              <th class="px-2 py-2 w-8 bg-surface-container-low border-b border-border" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="ch in commsStore.channels" :key="ch.channel_id" class="border-b border-border/60 last:border-0">
              <td v-for="col in COLUMNS" :key="col.key" class="p-0 border-r border-border/40 last:border-r-0">
                <input
                  :value="(ch as Record<string, unknown>)[col.key] as string ?? ''"
                  class="w-full bg-transparent text-on-surface font-mono text-[11px] px-2 py-1.5 border border-transparent focus:outline-none focus:bg-white focus:border-primary focus:shadow-[0_0_0_2px_rgba(23,80,158,0.18)] focus:relative focus:z-10 box-border hover:bg-surface-container-low"
                  @blur="updateCell(ch.channel_id, col.key, ($event.target as HTMLInputElement).value)"
                />
              </td>
              <td class="px-1 py-1 text-center w-8">
                <button class="w-6 h-6 inline-flex items-center justify-center bg-transparent border-none rounded text-outline hover:text-error hover:bg-surface-container-low cursor-pointer" @click="removeRow(ch.channel_id)">
                  <span class="material-symbols-outlined text-[16px]">close</span>
                </button>
              </td>
            </tr>
            <tr v-if="commsStore.channels.length === 0">
              <td :colspan="COLUMNS.length + 1" class="px-4 py-6 text-center font-body text-[13px] text-outline">
                No channels yet. Add from library or add a blank row.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ICP Phone + Prepared By (2-column) -->
    <div class="grid gap-3" style="grid-template-columns: 1fr 1fr;">
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">ICP Phone</h3>
        </div>
        <div class="p-3.5">
          <FosInput :model-value="op?.contact_phone ?? ''" placeholder="ICP contact phone…" type="tel" @blur="saveIcpPhone(($event.target as HTMLInputElement).value)" />
        </div>
      </div>
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
        </div>
        <div class="p-3.5">
          <FosInput :model-value="preparedBy205" placeholder="Name of person preparing ICS 205…" @blur="savePreparedBy(($event.target as HTMLInputElement).value)" />
        </div>
      </div>
    </div>

    <!-- Section footer -->
    <div class="flex items-center gap-2.5 mt-2 pt-3 border-t border-border/60">
      <button class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface cursor-pointer hover:bg-surface-container-low transition-colors" @click="$emit('prev')">
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        ICS 203
      </button>
      <span class="flex-1" />
      <button class="inline-flex items-center gap-1.5 bg-primary border border-primary rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-white cursor-pointer hover:opacity-90 transition-opacity" @click="$emit('next')">
        ICS 206
        <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
      </button>
    </div>

    <!-- Library dialog -->
    <FosDialog :open="showLibraryDialog" title="Channel Presets" @update:open="showLibraryDialog = $event">
      <div class="space-y-2 min-h-[100px]">
        <div v-for="preset in presets" :key="preset.preset_id" class="flex items-center justify-between p-3 border border-border rounded hover:bg-surface-container-high cursor-pointer" @click="addFromPreset(preset)">
          <div>
            <p class="text-sm font-semibold text-on-surface">{{ preset.name }}</p>
            <p class="text-xs text-outline font-mono">{{ preset.rx_freq }} / {{ preset.tx_freq }}</p>
          </div>
          <span class="material-symbols-outlined text-[18px] text-outline">add_circle</span>
        </div>
        <p v-if="presets.length === 0" class="text-sm text-outline py-4 text-center">No channel presets configured for your agency.</p>
      </div>
    </FosDialog>
  </div>
</template>
